/**
 * The /settings surface: the applicable parts of the Syncra settings mockup over real state,
 * grouped into four navigable sections (calls, calendar, account, notifications) switched by
 * a segmented row without a router - the choice lives in the ?section= query, rewritten in
 * place with history.replaceState (deep links: /settings?section=calendar). Calls holds the
 * device, background and camera-quality cards; Account the language, appearance and SSO
 * account blocks; Calendar the week-start and default-view preferences the /calendar page
 * reads (vc.calendarPrefs), the working-hours card (days + HH:MM range feeding FullCalendar's
 * inverse business-hours dimming) and the external-calendar sources block (CR-31/33) - CalDAV /
 * Exchange / AD rooms / iCalendar links managed through the calendar service with connection
 * checks; Notifications the meeting-reminder preferences (vc.notificationPrefs) and the
 * browser notification permission. Language switches the live i18n instance; devices and the
 * background mode write the shared device-preferences store the prejoin screen reads
 * (vc.devicePrefs); the account section mirrors the shared SSO session (sign-in returns to
 * `/`, sign-out clears it for every screen). The reminder loop itself runs app-wide from
 * the app shell, not here.
 */

import { ArrowLeft, Mic, Video, Volume2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { UserAvatar } from '../components/user-avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { DeviceSelect } from '../room/DeviceSelect';
import {
  browserStorage,
  loadDevicePrefs,
  saveDevicePrefs,
  EMPTY_DEVICE_PREFS,
} from '../room/devicePrefs';
import type { DevicePrefs, KeyValueStorage } from '../room/devicePrefs';
import type {
  BackgroundMode,
  BackgroundQualitySetting,
  ResolvedBackgroundQuality,
} from '../media/backgroundProcessor';
import type { CameraQualitySetting } from '../room/publishQuality';
import { deviceOptions, NO_DEVICE } from '../room/prejoinState';
import type { DeviceOption, InputDeviceInfoLike } from '../room/prejoinState';
import { navigateTo } from '../lib/spa';
import type { ViewId } from '../calendar/CalendarPage';
import {
  DEFAULT_CALENDAR_PREFS,
  isValidWorkingRange,
  loadCalendarPrefs,
  saveCalendarPrefs,
} from '../calendar/calendarPrefs';
import type { CalendarPrefs, StartOfWeek } from '../calendar/calendarPrefs';
import {
  DEFAULT_NOTIFICATION_PREFS,
  KNOWN_LEAD_MINUTES,
  loadNotificationPrefs,
  saveNotificationPrefs,
} from '../calendar/notificationPrefs';
import type { NotificationPrefs } from '../calendar/notificationPrefs';
import {
  checkSession,
  forgetSession,
  sessionUsername,
  startCalendarSignIn,
  calendarSsoConfig,
} from '../calendar/signIn';
import { SourcesSection } from '../calendar/SourcesSection';
import { browserSsoStorage, loadSsoSession } from '../auth/sso';
import { changeLanguage, type AppLanguage } from '../i18n';
import { cn } from '../lib/utils';

export interface SettingsPageProps {
  /** Injected for tests; defaults to the browser localStorage (device preferences). */
  readonly storage?: KeyValueStorage | null;
  /** Injected for tests; defaults to the browser's shared SSO store. */
  readonly ssoStorage?: KeyValueStorage | null;
  /** Injected for tests; defaults to the wall clock (session expiry check). */
  readonly now?: Date;
  /** Device enumeration override for tests; defaults to navigator.mediaDevices. */
  readonly enumerateDevices?: () => Promise<readonly InputDeviceInfoLike[]>;
  /** Notification API seam for tests; defaults to window.Notification (null = unsupported). */
  readonly notifications?: NotificationsApi | null;
}

/** The slice of the Notification API the permission row needs (structural - the DOM
 * Notification satisfies it; tests inject a scripted double). */
export interface NotificationsApi {
  readonly permission: () => NotificationPermission;
  readonly requestPermission: () => Promise<NotificationPermission>;
}

/** The real browser Notification API, or null where it does not exist (unsupported browser). */
export function browserNotifications(): NotificationsApi | null {
  if (typeof Notification === 'undefined') {
    return null;
  }
  return {
    permission: () => Notification.permission,
    requestPermission: () => Notification.requestPermission(),
  };
}

/** The navigable settings sections; the order is the switcher's order. */
export type SettingsSection = 'calls' | 'calendar' | 'account' | 'notifications';

const SECTIONS: readonly SettingsSection[] = ['calls', 'calendar', 'account', 'notifications'];

const SECTION_ITEMS: readonly {
  readonly id: SettingsSection;
  readonly labelKey:
    | 'settings.section_calls'
    | 'settings.section_calendar'
    | 'settings.section_account'
    | 'settings.section_notifications';
}[] = [
  { id: 'calls', labelKey: 'settings.section_calls' },
  { id: 'calendar', labelKey: 'settings.section_calendar' },
  { id: 'account', labelKey: 'settings.section_account' },
  { id: 'notifications', labelKey: 'settings.section_notifications' },
];

/** The ?section= query into the switcher's initial section; unknown or absent values land
 * on calls - the section the page led with before the switcher existed. */
export function sectionFromQuery(query: string): SettingsSection {
  const param = new URLSearchParams(query).get('section');
  return SECTIONS.find((id) => id === param) ?? 'calls';
}

const CARD = 'rounded-xl border border-border bg-card p-5';
const CAPS = 'text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground';

const BACKGROUND_MODES: readonly BackgroundMode[] = ['none', 'blur', 'office'];
const BACKGROUND_QUALITIES: readonly BackgroundQualitySetting[] = ['auto', 'low', 'high'];
const CAMERA_QUALITIES: readonly CameraQualitySetting[] = ['auto', 'low', 'medium', 'high'];

const START_OF_WEEK_CHOICES: readonly StartOfWeek[] = ['monday', 'saturday', 'sunday'];

/** The working-day chips in display order, Monday first: FullCalendar day numbers
 * (0 = Sunday) with the weekend rendered last. */
const WORK_DAY_CHOICES: readonly number[] = [1, 2, 3, 4, 5, 6, 0];

/** The default-view options; the labels reuse the calendar switcher's view names. */
const CALENDAR_VIEW_OPTIONS: readonly {
  readonly id: ViewId;
  readonly labelKey:
    | 'calendar.page.view_year'
    | 'calendar.page.view_month'
    | 'calendar.page.view_week'
    | 'calendar.page.view_work_week'
    | 'calendar.page.view_day';
}[] = [
  { id: 'multiMonthYear', labelKey: 'calendar.page.view_year' },
  { id: 'dayGridMonth', labelKey: 'calendar.page.view_month' },
  { id: 'timeGridWeek', labelKey: 'calendar.page.view_week' },
  { id: 'workWeek', labelKey: 'calendar.page.view_work_week' },
  { id: 'timeGridDay', labelKey: 'calendar.page.view_day' },
];

export function SettingsPage({
  storage,
  ssoStorage,
  now,
  enumerateDevices,
  notifications,
}: SettingsPageProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const prefsStore = useMemo(() => storage ?? browserStorage(), [storage]);
  const ssoStore = useMemo(() => ssoStorage ?? browserSsoStorage(), [ssoStorage]);
  const at = useMemo(() => now ?? new Date(), [now]);
  const notifApi = useMemo<NotificationsApi | null>(
    () => (notifications === undefined ? browserNotifications() : notifications),
    [notifications],
  );

  const [prefs, setPrefs] = useState<DevicePrefs>(() =>
    prefsStore === null ? EMPTY_DEVICE_PREFS : loadDevicePrefs(prefsStore),
  );
  const [calendar, setCalendar] = useState<CalendarPrefs>(() =>
    prefsStore === null ? DEFAULT_CALENDAR_PREFS : loadCalendarPrefs(prefsStore),
  );
  const [notif, setNotif] = useState<NotificationPrefs>(() =>
    prefsStore === null ? DEFAULT_NOTIFICATION_PREFS : loadNotificationPrefs(prefsStore),
  );
  const [notifPermission, setNotifPermission] = useState<NotificationPermission | null>(
    () => notifApi?.permission() ?? null,
  );
  const [cameraOptions, setCameraOptions] = useState<readonly DeviceOption[]>([]);
  const [micOptions, setMicOptions] = useState<readonly DeviceOption[]>([]);
  const [speakerOptions, setSpeakerOptions] = useState<readonly DeviceOption[]>([]);
  const [ssoSession, setSsoSession] = useState<'signed_in' | 'guest'>(() =>
    ssoStore !== null && checkSession(ssoStore, at) === 'signed_in' ? 'signed_in' : 'guest',
  );
  const [username, setUsername] = useState<string | null>(() => {
    if (ssoStore === null || checkSession(ssoStore, at) !== 'signed_in') {
      return null;
    }
    const stored = loadSsoSession(ssoStore);
    return stored === null ? null : sessionUsername(stored.accessToken);
  });

  // The bearer the calendar sources section calls the calendar service with, taken from the
  // shared SSO store the same way CalendarPage reads its own token (CalendarPage also runs
  // the auto-refresh loop; here the section relies on the client's 401-rotate-once healing).
  const sourcesToken = useMemo<string | null>(() => {
    if (ssoStore === null || ssoSession !== 'signed_in') {
      return null;
    }
    return loadSsoSession(ssoStore)?.accessToken ?? null;
  }, [ssoStore, ssoSession]);

  const currentLanguage: AppLanguage = i18n.language === 'ru' ? 'ru' : 'en';

  // The switcher starts from ?section= and rewrites it in place on every switch (no router,
  // no history entries - a settings deep link stays shareable).
  const [section, setSection] = useState<SettingsSection>(() =>
    sectionFromQuery(window.location.search),
  );

  function switchSection(next: SettingsSection): void {
    setSection(next);
    const url = new URL(window.location.href);
    url.searchParams.set('section', next);
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  }

  function updatePrefs(next: DevicePrefs): void {
    setPrefs(next);
    if (prefsStore !== null) {
      saveDevicePrefs(prefsStore, next);
    }
  }

  function updateCalendarPrefs(next: CalendarPrefs): void {
    setCalendar(next);
    if (prefsStore !== null) {
      saveCalendarPrefs(next, prefsStore);
    }
  }

  // The time inputs keep LOCAL drafts: an in-progress (invalid) pair shows the range error
  // and nothing is persisted - storage keeps the last valid pair until the draft heals.
  const [workStart, setWorkStart] = useState<string>(() => calendar.workingHours.start);
  const [workEnd, setWorkEnd] = useState<string>(() => calendar.workingHours.end);
  const workRangeValid = isValidWorkingRange(workStart, workEnd);

  function changeWorkTime(nextStart: string, nextEnd: string): void {
    setWorkStart(nextStart);
    setWorkEnd(nextEnd);
    if (isValidWorkingRange(nextStart, nextEnd)) {
      updateCalendarPrefs({
        ...calendar,
        workingHours: { ...calendar.workingHours, start: nextStart, end: nextEnd },
      });
    }
  }

  function toggleWorkingDay(day: number): void {
    const days = calendar.workingHours.days;
    const nextDays = days.includes(day)
      ? days.filter((entry) => entry !== day)
      : [...days, day].sort((a, b) => a - b);
    if (nextDays.length === 0) {
      return; // the last enabled day cannot be turned off
    }
    // Chips always write the PERSISTED times (the last valid pair), not the drafts: with an
    // invalid time draft the storage gains the new days while keeping the old times.
    updateCalendarPrefs({ ...calendar, workingHours: { ...calendar.workingHours, days: nextDays } });
  }

  function updateNotificationPrefs(next: NotificationPrefs): void {
    setNotif(next);
    if (prefsStore !== null) {
      saveNotificationPrefs(next, prefsStore);
    }
  }

  // The permission row: asks the browser once per click; the answer (granted or denied)
  // replaces the row's state. A rejection (older browsers throwing on request) keeps the
  // previous state and the button retries.
  async function requestNotifPermission(): Promise<void> {
    if (notifApi === null) {
      return;
    }
    try {
      setNotifPermission(await notifApi.requestPermission());
    } catch {
      // keep showing the current state; the button retries
    }
  }

  // One enumeration on mount; without permission the browser returns devices with empty
  // labels, which deviceOptions() turns into generic numbered names.
  useEffect(() => {
    let cancelled = false;
    const enumerate =
      enumerateDevices ??
      ((): Promise<readonly InputDeviceInfoLike[]> => {
        const media = navigator.mediaDevices;
        if (media === undefined) {
          return Promise.resolve([]);
        }
        return media.enumerateDevices();
      });
    void enumerate()
      .then((devices) => {
        if (cancelled) {
          return;
        }
        setCameraOptions(deviceOptions(devices, 'videoinput'));
        setMicOptions(deviceOptions(devices, 'audioinput'));
        setSpeakerOptions(deviceOptions(devices, 'audiooutput'));
      })
      .catch(() => {
        // No device list (blocked permission, insecure context): the selects stay empty and
        // the defaults apply, exactly like the prejoin screen.
      });
    return () => {
      cancelled = true;
    };
  }, [enumerateDevices]);

  const handleSignIn = useCallback(async (): Promise<void> => {
    if (ssoStore === null) {
      return;
    }
    try {
      await startCalendarSignIn(
        calendarSsoConfig(),
        window.location.origin,
        ssoStore,
        window.location,
        '/',
      );
    } catch {
      // staying on the page; the button retries
    }
  }, [ssoStore]);

  function handleSignOut(): void {
    if (ssoStore !== null) {
      forgetSession(ssoStore);
    }
    setSsoSession('guest');
    setUsername(null);
  }

  return (
    <div
      data-testid="settings-page"
      className="flex h-screen flex-col overflow-hidden bg-background text-foreground"
    >
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-card px-4 sm:px-6">
        <Button
          variant="ghost"
          size="icon"
          className="size-9 text-muted-foreground hover:text-foreground"
          aria-label={t('settings.back_aria')}
          data-testid="settings-back"
          onClick={() => navigateTo('/')}
        >
          <ArrowLeft aria-hidden className="size-4" />
        </Button>
        <h1 className="truncate text-[15px] font-semibold tracking-tight text-foreground">
          {t('settings.page_title')}
        </h1>
      </header>

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[760px] space-y-6 px-4 py-6 sm:px-6 lg:px-8">
          {/* ---------- Section switcher (calls / calendar / account) ---------- */}
          <div
            className="flex gap-2"
            role="group"
            aria-label={t('settings.page_title')}
            data-testid="settings-sections"
          >
            {SECTION_ITEMS.map((item) => (
              <button
                key={item.id}
                type="button"
                data-testid={`settings-tab-${item.id}`}
                aria-pressed={section === item.id}
                onClick={() => {
                  switchSection(item.id);
                }}
                className={cn(
                  'rounded-lg border px-4 py-2 text-[13px] font-medium transition-colors',
                  section === item.id
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                {t(item.labelKey)}
              </button>
            ))}
          </div>

          {section === 'calls' ? (
            <>
              {/* ---------- Devices ---------- */}
              <section
                aria-labelledby="settings-devices-title"
                className={CARD}
                data-testid="settings-devices"
              >
                <h2 id="settings-devices-title" className={CAPS}>
                  {t('settings.section_devices')}
                </h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-3">
                  <DeviceSelect
                    id="settings-camera"
                    label={t('prejoin.camera_label')}
                    icon={Video}
                    value={prefs.cameraDeviceId ?? NO_DEVICE}
                    options={cameraOptions}
                    noDeviceLabel={t('prejoin.no_camera')}
                    testid="settings-camera-select"
                    onChange={(deviceId) => {
                      updatePrefs({
                        ...prefs,
                        cameraDeviceId: deviceId === NO_DEVICE ? null : deviceId,
                      });
                    }}
                  />
                  <DeviceSelect
                    id="settings-microphone"
                    label={t('prejoin.mic_label')}
                    icon={Mic}
                    value={prefs.micDeviceId ?? NO_DEVICE}
                    options={micOptions}
                    noDeviceLabel={t('prejoin.no_microphone')}
                    testid="settings-microphone-select"
                    onChange={(deviceId) => {
                      updatePrefs({
                        ...prefs,
                        micDeviceId: deviceId === NO_DEVICE ? null : deviceId,
                      });
                    }}
                  />
                  <DeviceSelect
                    id="settings-speakers"
                    label={t('prejoin.speakers_label')}
                    icon={Volume2}
                    value={prefs.speakerDeviceId ?? NO_DEVICE}
                    options={speakerOptions}
                    noDeviceLabel={t('prejoin.speakers_default')}
                    testid="settings-speakers-select"
                    onChange={(deviceId) => {
                      updatePrefs({
                        ...prefs,
                        speakerDeviceId: deviceId === NO_DEVICE ? null : deviceId,
                      });
                    }}
                  />
                </div>

                <div className="mt-5">
                  <p className="mb-2 text-[12px] font-medium text-muted-foreground">
                    {t('settings.bg_mode')}
                  </p>
                  <div className="flex gap-1.5" role="group" aria-label={t('settings.bg_mode')}>
                    {BACKGROUND_MODES.map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        data-testid={`settings-bg-${mode}`}
                        aria-pressed={prefs.bgMode === mode}
                        onClick={() => {
                          updatePrefs({ ...prefs, bgMode: mode });
                        }}
                        className={cn(
                          'rounded-lg border px-3 py-1.5 text-[12.5px] font-medium transition-colors',
                          prefs.bgMode === mode
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
                        )}
                      >
                        {t(`prejoin.bg_${mode}`)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-4">
                  <p className="mb-2 text-[12px] font-medium text-muted-foreground">
                    {t('settings.bg_quality')}
                  </p>
                  <div className="flex gap-1.5" role="group" aria-label={t('settings.bg_quality')}>
                    {BACKGROUND_QUALITIES.map((quality) => (
                      <button
                        key={quality}
                        type="button"
                        data-testid={`settings-quality-${quality}`}
                        aria-pressed={prefs.bgQuality === quality}
                        onClick={() => {
                          updatePrefs({ ...prefs, bgQuality: quality });
                        }}
                        className={cn(
                          'rounded-lg border px-3 py-1.5 text-[12.5px] font-medium transition-colors',
                          prefs.bgQuality === quality
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
                        )}
                      >
                        {t(`prejoin.bg_quality_${quality}`)}
                      </button>
                    ))}
                  </div>
                  <ResolvedHint resolved={prefs.resolvedQuality} quality={prefs.bgQuality} />
                </div>

                <div className="mt-4">
                  <p className="mb-2 text-[12px] font-medium text-muted-foreground">
                    {t('settings.cam_quality')}
                  </p>
                  <div className="flex gap-1.5" role="group" aria-label={t('settings.cam_quality')}>
                    {CAMERA_QUALITIES.map((quality) => (
                      <button
                        key={quality}
                        type="button"
                        data-testid={`settings-cam-quality-${quality}`}
                        aria-pressed={prefs.camQuality === quality}
                        onClick={() => {
                          updatePrefs({ ...prefs, camQuality: quality });
                        }}
                        className={cn(
                          'rounded-lg border px-3 py-1.5 text-[12.5px] font-medium transition-colors',
                          prefs.camQuality === quality
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
                        )}
                      >
                        {t(`settings.cam_quality_${quality}`)}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-[11.5px] text-muted-foreground">
                    {t('settings.cam_quality_note')}
                  </p>
                </div>

                <div className="mt-5 divide-y divide-border border-t border-border/70">
                  <ToggleRow
                    title={t('prejoin.opt_mirror')}
                    checked={prefs.mirrorVideo}
                    testid="settings-toggle-mirror"
                    onChecked={(checked) => {
                      updatePrefs({ ...prefs, mirrorVideo: checked });
                    }}
                  />
                  <ToggleRow
                    title={t('prejoin.opt_audio_join')}
                    checked={prefs.joinMicOn}
                    testid="settings-toggle-audio-join"
                    onChecked={(checked) => {
                      updatePrefs({ ...prefs, joinMicOn: checked });
                    }}
                  />
                </div>
              </section>
            </>
          ) : null}

          {section === 'calendar' ? (
            <>
              {/* ---------- Calendar preferences ---------- */}
              <section
                aria-labelledby="settings-calendar-title"
                className={CARD}
                data-testid="settings-calendar"
              >
                <h2 id="settings-calendar-title" className={CAPS}>
                  {t('settings.section_calendar')}
                </h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <SelectRow
                    id="settings-start-of-week"
                    label={t('settings.cal_start_of_week')}
                    testid="settings-start-of-week"
                    value={calendar.startOfWeek}
                    options={START_OF_WEEK_CHOICES.map((day) => ({
                      value: day,
                      label: t(`settings.cal_week_${day}`),
                    }))}
                    onChange={(value) => {
                      const match = START_OF_WEEK_CHOICES.find((day) => day === value);
                      if (match !== undefined) {
                        updateCalendarPrefs({ ...calendar, startOfWeek: match });
                      }
                    }}
                  />
                  <SelectRow
                    id="settings-default-view"
                    label={t('settings.cal_default_view')}
                    testid="settings-default-view"
                    value={calendar.defaultView}
                    options={CALENDAR_VIEW_OPTIONS.map((choice) => ({
                      value: choice.id,
                      label: t(choice.labelKey),
                    }))}
                    onChange={(value) => {
                      const match = CALENDAR_VIEW_OPTIONS.find((choice) => choice.id === value);
                      if (match !== undefined) {
                        updateCalendarPrefs({ ...calendar, defaultView: match.id });
                      }
                    }}
                  />
                </div>
                <p className="mt-3 text-[11.5px] text-muted-foreground">
                  {t('settings.cal_default_view_hint')}
                </p>
              </section>

              {/* ---------- Working hours (highlighted band in the calendar grid) ---------- */}
              <section
                aria-labelledby="settings-working-hours-title"
                className={CARD}
                data-testid="settings-working-hours"
              >
                <h2 id="settings-working-hours-title" className={CAPS}>
                  {t('settings.work_hours_title')}
                </h2>
                <p className="mt-1 text-[12px] text-muted-foreground">
                  {t('settings.work_hours_hint')}
                </p>
                <div
                  className="mt-4 flex gap-1.5"
                  role="group"
                  aria-label={t('settings.work_hours_title')}
                >
                  {WORK_DAY_CHOICES.map((day) => (
                    <button
                      key={day}
                      type="button"
                      data-testid={`settings-working-day-${day}`}
                      aria-pressed={calendar.workingHours.days.includes(day)}
                      onClick={() => {
                        toggleWorkingDay(day);
                      }}
                      className={cn(
                        'rounded-lg border px-3 py-1.5 text-[12.5px] font-medium transition-colors',
                        calendar.workingHours.days.includes(day)
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
                      )}
                    >
                      {t(`settings.work_day_${day}`)}
                    </button>
                  ))}
                </div>
                <div className="mt-4 grid max-w-[360px] gap-4 sm:grid-cols-2">
                  <label htmlFor="settings-work-start" className="block">
                    <span className="mb-2 block text-[12px] font-medium text-muted-foreground">
                      {t('settings.work_start')}
                    </span>
                    <Input
                      id="settings-work-start"
                      type="time"
                      data-testid="settings-work-start"
                      value={workStart}
                      onChange={(event) => {
                        changeWorkTime(event.target.value, workEnd);
                      }}
                      className="dark:[color-scheme:dark]"
                    />
                  </label>
                  <label htmlFor="settings-work-end" className="block">
                    <span className="mb-2 block text-[12px] font-medium text-muted-foreground">
                      {t('settings.work_end')}
                    </span>
                    <Input
                      id="settings-work-end"
                      type="time"
                      data-testid="settings-work-end"
                      value={workEnd}
                      onChange={(event) => {
                        changeWorkTime(workStart, event.target.value);
                      }}
                      className="dark:[color-scheme:dark]"
                    />
                  </label>
                </div>
                {!workRangeValid ? (
                  <p
                    role="alert"
                    className="mt-2 text-[11.5px] text-red-400"
                    data-testid="settings-work-range-error"
                  >
                    {t('settings.work_hours_range_error')}
                  </p>
                ) : null}
              </section>

              {/* ---------- External calendar sources (CR-31/33) ---------- */}
              <section
                aria-labelledby="settings-calendar-sources-title"
                className={CARD}
                data-testid="settings-calendar-sources"
              >
                <h2 id="settings-calendar-sources-title" className={CAPS}>
                  {t('settings.cal_integrations_title')}
                </h2>
                <p className="mt-1 text-[12px] text-muted-foreground">
                  {t('settings.cal_integrations_hint')}
                </p>
                <SourcesSection token={sourcesToken} />
              </section>
            </>
          ) : null}

          {section === 'notifications' ? (
            <>
              {/* ---------- Meeting reminders ---------- */}
              <section
                aria-labelledby="settings-notifications-title"
                className={CARD}
                data-testid="settings-notifications"
              >
                <h2 id="settings-notifications-title" className={CAPS}>
                  {t('settings.section_notifications')}
                </h2>
                {notifPermission === null ? (
                  <p
                    className="mt-3 text-[12px] text-muted-foreground"
                    data-testid="settings-notif-unsupported"
                  >
                    {t('settings.notif_unsupported')}
                  </p>
                ) : null}
                <div className="mt-3 divide-y divide-border border-t border-border/70">
                  <ToggleRow
                    title={t('settings.notif_enable')}
                    hint={t('settings.notif_enable_hint')}
                    checked={notif.enabled}
                    testid="settings-notif-toggle"
                    disabled={notifPermission === null}
                    onChecked={(checked) => {
                      updateNotificationPrefs({ ...notif, enabled: checked });
                    }}
                  />
                </div>
                <div className="mt-4 max-w-[280px]">
                  <SelectRow
                    id="settings-notif-lead"
                    label={t('settings.notif_lead')}
                    testid="settings-notif-lead"
                    value={String(notif.leadMinutes)}
                    disabled={notifPermission === null}
                    options={KNOWN_LEAD_MINUTES.map((lead) => ({
                      value: String(lead),
                      label: t('settings.notif_lead_minutes', { count: lead }),
                    }))}
                    onChange={(value) => {
                      const match = KNOWN_LEAD_MINUTES.find(
                        (lead) => String(lead) === value,
                      );
                      if (match !== undefined) {
                        updateNotificationPrefs({ ...notif, leadMinutes: match });
                      }
                    }}
                  />
                </div>
                {notifPermission !== null ? (
                  <div
                    className="mt-4 flex items-center justify-between gap-4 border-t border-border/70 pt-4"
                    data-testid="settings-notif-permission"
                  >
                    <p className="text-[12.5px] text-muted-foreground">
                      {notifPermission === 'granted'
                        ? t('settings.notif_permission_granted')
                        : notifPermission === 'denied'
                          ? t('settings.notif_permission_denied')
                          : t('settings.notif_permission_default')}
                    </p>
                    {notifPermission === 'default' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 shrink-0 gap-1.5 text-[12.5px] font-semibold"
                        data-testid="settings-notif-request"
                        onClick={() => {
                          void requestNotifPermission();
                        }}
                      >
                        {t('settings.notif_permission_request')}
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </section>
            </>
          ) : null}

          {section === 'account' ? (
            <>
              {/* ---------- Language ---------- */}
              <section
                aria-labelledby="settings-language-title"
                className={CARD}
                data-testid="settings-language"
              >
                <h2 id="settings-language-title" className={CAPS}>
                  {t('settings.section_language')}
                </h2>
                <div
                  className="mt-4 flex gap-2"
                  role="group"
                  aria-label={t('settings.section_language')}
                >
                  {(['en', 'ru'] as const).map((language) => (
                    <button
                      key={language}
                      type="button"
                      data-testid={`settings-lang-${language}`}
                      aria-pressed={currentLanguage === language}
                      onClick={() => {
                        void changeLanguage(language);
                      }}
                      className={cn(
                        'rounded-lg border px-4 py-2 text-[13px] font-medium transition-colors',
                        currentLanguage === language
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
                      )}
                    >
                      {t(`lang.name_${language}`)}
                    </button>
                  ))}
                </div>
                <p className="mt-3 text-[12px] text-muted-foreground">
                  {t('settings.language_hint')}
                </p>
              </section>

              {/* ---------- Appearance (dark only) ---------- */}
              <section
                aria-labelledby="settings-appearance-title"
                className={CARD}
                data-testid="settings-appearance"
              >
                <h2 id="settings-appearance-title" className={CAPS}>
                  {t('settings.section_appearance')}
                </h2>
                <p className="mt-3 flex items-center gap-1.5 text-[13px] text-foreground">
                  <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-emerald-400" />
                  {t('settings.appearance_dark')}
                </p>
                <p className="mt-1 text-[12px] text-muted-foreground">
                  {t('settings.appearance_dark_hint')}
                </p>
              </section>

              {/* ---------- Account ---------- */}
              <section
                aria-labelledby="settings-account-title"
                className={CARD}
                data-testid="settings-account"
              >
                <h2 id="settings-account-title" className={CAPS}>
                  {t('settings.section_account')}
                </h2>
                {ssoSession === 'signed_in' ? (
                  <div className="mt-4 flex items-center gap-3">
                    <UserAvatar name={username ?? 'SSO'} size="md" />
                    <div className="min-w-0 flex-1">
                      <p
                        className="truncate text-[13.5px] font-semibold text-foreground"
                        data-testid="settings-account-name"
                      >
                        {username ?? 'SSO'}
                      </p>
                      <p className="truncate text-[11.5px] text-muted-foreground">
                        {t('settings.account_via_sso')}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 shrink-0 gap-1.5 text-[12.5px] font-semibold"
                      data-testid="settings-signout"
                      onClick={handleSignOut}
                    >
                      {t('sso.sign_out')}
                    </Button>
                  </div>
                ) : (
                  <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-[13px] text-muted-foreground">
                      {t('settings.account_guest')}
                    </p>
                    <Button
                      size="sm"
                      className="h-8 shrink-0 gap-1.5 text-[12.5px] font-semibold"
                      data-testid="settings-signin"
                      onClick={() => {
                        void handleSignIn();
                      }}
                    >
                      {t('sso.sign_in')}
                    </Button>
                  </div>
                )}
              </section>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ResolvedHint({
  resolved,
  quality,
}: {
  readonly resolved: ResolvedBackgroundQuality | null;
  readonly quality: BackgroundQualitySetting;
}): JSX.Element {
  const { t } = useTranslation();
  if (quality !== 'auto' || resolved === null) {
    return (
      <p className="mt-2 text-[11.5px] text-muted-foreground">{t('prejoin.bg_quality_note')}</p>
    );
  }
  return (
    <p
      className="mt-2 text-[11.5px] font-medium text-foreground"
      data-testid="settings-quality-resolved"
    >
      {t('settings.quality_resolved', {
        level: t(resolved === 'low' ? 'prejoin.bg_quality_low' : 'prejoin.bg_quality_high'),
      })}
    </p>
  );
}

function SelectRow({
  id,
  label,
  value,
  testid,
  options,
  disabled = false,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly testid: string;
  readonly options: readonly { readonly value: string; readonly label: string }[];
  readonly disabled?: boolean;
  readonly onChange: (value: string) => void;
}): JSX.Element {
  return (
    <label htmlFor={id} className="block">
      <span className="mb-2 block text-[12px] font-medium text-muted-foreground">{label}</span>
      <select
        id={id}
        data-testid={testid}
        value={value}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className="h-9 w-full cursor-pointer appearance-none rounded-lg border border-border bg-card px-3 text-[13px] text-foreground outline-none transition-[color,border-color,box-shadow] hover:bg-accent/50 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:[color-scheme:dark]"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function ToggleRow({
  title,
  hint,
  checked,
  testid,
  disabled = false,
  onChecked,
}: {
  readonly title: string;
  readonly hint?: string;
  readonly checked: boolean;
  readonly testid: string;
  readonly disabled?: boolean;
  readonly onChecked: (checked: boolean) => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4 py-3.5">
      <div className="min-w-0">
        <p className="text-[13px] font-medium text-foreground">{title}</p>
        {hint !== undefined ? (
          <p className="mt-0.5 text-[11.5px] text-muted-foreground">{hint}</p>
        ) : null}
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onChecked}
        aria-label={title}
        data-testid={testid}
        disabled={disabled}
      />
    </div>
  );
}
